import { LENGTHS } from '../ui/DataTable'
import type { TicketXdrDetection } from '../api/securitee'

/**
 * Filtrarea, sortarea si paginarea detectiilor din fila XDR a tichetului - tot
 * in browser, pe cele cel mult 200 aduse (scope incident / device 30 zile).
 *
 * Partea fara DOM e aici, ca sa fie probata fara sa randam: optiunile din
 * lista (reguli, tehnici MITRE, cu numar), filtrul combinat cu SI, sortarea pe
 * timp si taierea pe pagina. UI-ul (`TicketXdrTab`) tine doar starea.
 */

/** Marimile de pagina: cele din portal (DataTable) + "All" pentru o lista de maxim 200. */
export const PAGE_SIZES = [...LENGTHS, 'all'] as const
export type PageSize = (typeof PAGE_SIZES)[number]
export const DEFAULT_PAGE_SIZE: PageSize = 25

export type SortOrder = 'newest' | 'oldest'

export interface XdrFilters {
  /** Gravitatile bifate; gol = toate. Valorile sunt cele din detectie (low/medium/high/critical). */
  severities: ReadonlySet<string>
  /** rule_id-urile bifate; gol = toate. */
  rules: ReadonlySet<string>
  /** Tehnicile MITRE bifate (T1110...); gol = toate. */
  mitre: ReadonlySet<string>
  /** Cautare in titlu + rule_id, fara diacritice de care sa ne pese. */
  text: string
  /** Numai detectiile incidentului acestui tichet (ascunde restul pe "30 days"). */
  onlyCurrent: boolean
  order: SortOrder
}

export const EMPTY_FILTERS: XdrFilters = {
  severities: new Set(),
  rules: new Set(),
  mitre: new Set(),
  text: '',
  onlyCurrent: false,
  order: 'newest',
}

/** Cate filtre sunt active (pentru insigna si butonul Clear). */
export function activeFilterCount(f: XdrFilters): number {
  return (
    (f.severities.size > 0 ? 1 : 0) +
    (f.rules.size > 0 ? 1 : 0) +
    (f.mitre.size > 0 ? 1 : 0) +
    (f.text.trim() !== '' ? 1 : 0) +
    (f.onlyCurrent ? 1 : 0)
  )
}

/** Tehnicile MITRE dintr-o detectie: `d.mitre` poate tine mai multe ("T1059.001, T1027"). */
export function mitreTechniques(d: Pick<TicketXdrDetection, 'mitre'>): string[] {
  return d.mitre
    .split(/[,\s]+/)
    .map((t) => t.trim())
    .filter((t) => t !== '')
}

export interface Option {
  value: string
  label: string
  count: number
}

/** Regulile prezente in lista, cu numar si un titlu reprezentativ, sortate dupa rule_id. */
export function ruleOptions(detections: readonly TicketXdrDetection[]): Option[] {
  const by = new Map<string, { label: string; count: number }>()

  for (const d of detections) {
    const seen = by.get(d.rule_id)
    if (seen) seen.count++
    else by.set(d.rule_id, { label: d.title, count: 1 })
  }

  return [...by.entries()]
    .map(([value, { label, count }]) => ({ value, label, count }))
    .sort((a, b) => a.value.localeCompare(b.value))
}

/** Tehnicile MITRE prezente in lista, cu numar, sortate. */
export function mitreOptions(detections: readonly TicketXdrDetection[]): Option[] {
  const count = new Map<string, number>()

  for (const d of detections) {
    for (const t of mitreTechniques(d)) count.set(t, (count.get(t) ?? 0) + 1)
  }

  return [...count.entries()]
    .map(([value, n]) => ({ value, label: value, count: n }))
    .sort((a, b) => a.value.localeCompare(b.value))
}

/** Detectiile care trec de filtre (toate active restrang impreuna), apoi sortate pe timp. */
export function applyFilters(detections: readonly TicketXdrDetection[], f: XdrFilters): TicketXdrDetection[] {
  const text = f.text.trim().toLowerCase()

  const kept = detections.filter((d) => {
    if (f.onlyCurrent && !d.current) return false
    if (f.severities.size > 0 && !f.severities.has(d.severity)) return false
    if (f.rules.size > 0 && !f.rules.has(d.rule_id)) return false
    if (f.mitre.size > 0 && !mitreTechniques(d).some((t) => f.mitre.has(t))) return false
    if (text !== '' && !(`${d.title} ${d.rule_id}`.toLowerCase().includes(text))) return false

    return true
  })

  kept.sort((a, b) => (f.order === 'newest' ? b.detected_at.localeCompare(a.detected_at) : a.detected_at.localeCompare(b.detected_at)))

  return kept
}

/** Numarul de pagini pentru lista filtrata, la marimea aleasa (minim 1). */
export function pageCount(total: number, size: PageSize): number {
  if (size === 'all') return 1

  return Math.max(1, Math.ceil(total / size))
}

/** Felia unei pagini (pagina e 1-based; "all" da tot). */
export function pageSlice<T>(rows: readonly T[], size: PageSize, page: number): T[] {
  if (size === 'all') return [...rows]

  const start = (page - 1) * size

  return rows.slice(start, start + size)
}
